import { ReactNode, useCallback, useContext } from 'react';
import { Button } from '@shared/components/Button';
import { Modal } from '@shared/components/Modal';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { ColorThemeContext } from '@shared/styles';
import { i18n } from '@shared/util/i18n';

export interface UpgradeModalProps {
  title: string;
  description: string;
  isOpen: boolean;
  header?: ReactNode;
  onOpenChange(isOpen: boolean): void;
  onUpgrade(): void;
  onLearnMore?(): void;
}

export function UpgradeModal({
  title,
  description,
  isOpen,
  header,
  onOpenChange,
  onUpgrade,
  onLearnMore,
}: UpgradeModalProps) {
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  const handleUpgrade = useCallback(() => {
    close();
    onUpgrade();
  }, [onUpgrade, close]);
  const handleLearnMore = useCallback(() => {
    close();
    onLearnMore?.();
  }, [onLearnMore, close]);

  return (
    <Modal
      mode="modal"
      isOpen={isOpen}
      lightDismiss
      onOpenChange={onOpenChange}
      testID="upgrade-modal"
    >
      <Modal.TitleBar startContent={<Modal.CloseButton onClick={close} />} showBorder={false} />
      <UpgradeContent
        title={title}
        description={description}
        header={header}
        onUpgrade={handleUpgrade}
        onLearnMore={onLearnMore ? handleLearnMore : undefined}
        onDismiss={close}
      />
    </Modal>
  );
}

export interface UpgradeContentProps {
  title: string;
  description: string;
  header?: ReactNode;
  onUpgrade?: () => void;
  onLearnMore?: () => void;
  onDismiss?: () => void;
}

export function UpgradeContent({
  title,
  description,
  header,
  onUpgrade,
  onLearnMore,
  onDismiss,
}: UpgradeContentProps) {
  const colors = useContext(ColorThemeContext);

  return (
    <View center paddingHorizontal={4} paddingBottom={4}>
      {header ?? <MBIconV2 name="lock" color={colors.accent} />}
      <View paddingTop={2} />

      <Text size="body1" weight="semibold">
        {title}
      </Text>
      <View row paddingTop={1} />
      <Text size="body2" style={{ textAlign: 'center' }}>
        {description}
      </Text>
      <View row paddingTop={4} gap={1}>
        {!!onLearnMore && (
          <Button
            variant="ghost"
            size="large"
            label={i18n('common.learnMore')}
            onClick={onLearnMore}
          />
        )}
        {!onLearnMore && !!onDismiss && (
          <Button
            variant="ghost"
            size="large"
            label={!!onUpgrade ? i18n('common.close') : i18n('common.ok')}
            onClick={onDismiss}
          />
        )}
        {!!onUpgrade && (
          <Button
            variant="primary"
            size="large"
            label={i18n('common.upgrade')}
            onClick={onUpgrade}
          />
        )}
      </View>
    </View>
  );
}
